import * as d3 from 'd3';
import { ChartDataArray } from '../data/ChartDataArray';
import { LineChartConfig } from '../data/LineChartConfig';
import { ChartRenderer } from './ChartRenderer';
import { RendererConfig } from './RendererConfig';
import { LegendRenderer } from './LegendRenderer';
import { Rectangle } from './Rectange';
import { LinearAxisRenderer } from './LineAxisChartRenderer';
import { BandAxisRenderer } from './BandAxisRenderer';
import { LineRenderer } from './LineRenderer';
import { DataRenderer } from './DataRenderer';
import { HighlightRenderer } from './HighlightRenderer';
import { StackedAreaRenderer } from './StackedAreaRenderer';
import { ChartDataType } from '../data/ChartDataType';
import { ChartDataComparisonType } from '../data/ChartDataComparisonType';
import { TooltipRenderer } from './TooltipRenderer';
import { ChartRendererContext } from './ChartRendererContext';
import { DetectionDateMarkerRenderer } from './DetectionDateMarkerRenderer';

export class LineChartRenderer extends ChartRenderer {
  CHANGE_CHART_HEIGHT = 50;

  private data: ChartDataArray[] = [];

  private rerenderConfig?: RendererConfig;

  constructor(protected config: LineChartConfig) {
    super();
  }

  render(el: HTMLElement, context: ChartRendererContext) {
    this.data = this.config.data!;
    this.rerenderConfig = new RendererConfig(this.config);

    const svg = d3
      .select(el)
      .append('svg')
      .attr('class', 'mnb-chart mnb-line-chart line-chart')
      .attr('width', '100%')
      .attr('height', '100%');

    const dimensions: { height: number; width: number } = ChartRenderer.getDimensions(el);

    let legendHeight = 0;

    if (!this.config.legendConfig.hide) {
      legendHeight = new LegendRenderer(context, this.config).render(svg, this.data, dimensions);
    }

    const chartRect: Rectangle = {
      x: 0,
      y: 5,
      width: dimensions.width - 1,
      height: dimensions.height - 5 - legendHeight,
    };

    const lineChartRect: Rectangle = {
      x: chartRect.x,
      y: chartRect.y,
      width: chartRect.width,
      height: chartRect.height - (this.config.showChange ? this.CHANGE_CHART_HEIGHT : 0),
    };

    this.gElement = svg.append('g');

    const yAxisRenderer = new LinearAxisRenderer(
      this.gElement,
      context,
      this.config,
      false,
      this.rerenderConfig.hasSecondaryAxis()
    );
    const xAxisRenderer = new BandAxisRenderer(this.gElement, context, this.config, false);

    const xAxisHeight = xAxisRenderer.calcXHeight();
    const innerHeight = lineChartRect.height - xAxisHeight;

    const yAxis = yAxisRenderer.renderY(lineChartRect.width, innerHeight);

    let secondaryYAxis: {
      scale: d3.ScaleLinear<number, number>;
      width: number;
    } | null = null;

    if (this.rerenderConfig.hasSecondaryAxis()) {
      const secondaryYAxisRenderer = new LinearAxisRenderer(this.gElement, context, this.config, true, true);

      secondaryYAxis = secondaryYAxisRenderer.renderY(lineChartRect.width - yAxis.width, innerHeight);
    }

    const innerRect: Rectangle = {
      x: lineChartRect.x + yAxis.width,
      y: lineChartRect.y,
      width:
        lineChartRect.width -
        yAxis.width -
        (secondaryYAxis ? secondaryYAxis.width : 0) -
        (this.config.removeOuterSpacing ? 5 : 0),
      height: innerHeight,
    };

    this.gElement.attr('transform', `translate(${innerRect.x},${innerRect.y})`);

    const xAxis = xAxisRenderer.renderX(innerRect.width, innerRect.height);

    const lineRenderers: LineRenderer[] = [];
    const dataRenderers: DataRenderer[] = [];

    // Draw only if has more than one line or xAxis is not hidden
    if (this.config.data!.length > 1 || !this.config.xAxisConfig.hide) {
      // draw highlight lines here so that they are rendered beneath the data lines, guides, etc
      const highlightRenderer = new HighlightRenderer(
        this.gElement,
        this.config.removeOuterSpacing,
        this.config.highlightLineColor
      );
      highlightRenderer.render(xAxis.scale, yAxis.scale);
      dataRenderers.push(highlightRenderer);
    }

    switch (this.config.breakdownMode) {
      case 'stacked': {
        const stackedAreaRenderer = new StackedAreaRenderer(this.gElement);
        stackedAreaRenderer.render(this.config.getDataArrays(ChartDataType.breakdown), xAxis.scale, yAxis.scale);
        dataRenderers.push(stackedAreaRenderer);
        break;
      }
      case 'fill-stacked': {
        const fillStackedAreaRenderer = new StackedAreaRenderer(this.gElement, true);
        fillStackedAreaRenderer.render(this.config.getDataArrays(ChartDataType.breakdown), xAxis.scale, yAxis.scale);
        dataRenderers.push(fillStackedAreaRenderer);
        break;
      }
      case 'parallel':
        this.config
          .getDataArrays(ChartDataType.breakdown)
          .forEach((array, i) =>
            lineRenderers.push(new LineRenderer(this.gElement!, array, i, undefined, this.config.removeOuterSpacing))
          );
        break;
      default:
        this.config.data!.forEach(array =>
          lineRenderers.push(
            new LineRenderer(
              this.gElement!,
              array,
              undefined,
              array.type === ChartDataType.secondary && this.rerenderConfig!.hasSecondaryAxis(),
              this.config.removeOuterSpacing
            )
          )
        );
    }

    if (lineRenderers.length) {
      // render lines in reverse order -> lines that were added to
      // lineRenderers first will have the highest "z-index"
      lineRenderers
        .reverse()
        .forEach(line => line.render(xAxis.scale, !line.secondaryAxis ? yAxis.scale : secondaryYAxis!.scale));
      lineRenderers.forEach(line =>
        line.renderGuides(xAxis.scale, !line.secondaryAxis ? yAxis.scale : secondaryYAxis!.scale)
      );

      if (this.config.detectionDateMarker) {
        const primarySeries = this.config.getDataArray(ChartDataType.primary);
        if (primarySeries) {
          const detectionDateMarker = new DetectionDateMarkerRenderer(
            this.gElement,
            {
              color: this.config.detectionDateMarker.color,
              focusRingColor: this.config.detectionDateMarker.focusRingColor,
            },
            this.config.removeOuterSpacing
          );
          detectionDateMarker.render(xAxis.scale, yAxis.scale, primarySeries);
          dataRenderers.push(detectionDateMarker);
        }
      }

      if (this.config.showChange && this.rerenderConfig.hasComparisonData()) {
        const changeRect: Rectangle = {
          x: innerRect.x,
          y: innerRect.y + innerRect.height + xAxisHeight,
          width: innerRect.width,
          height: chartRect.height - innerRect.height - xAxisHeight,
        };

        this.drawChange(svg, changeRect, xAxis.scale, yAxis.formatter);
      }
    }

    new TooltipRenderer(
      this.config,
      this.tooltipContent!,
      [...lineRenderers, ...dataRenderers],
      this.config.breakdownMode === 'parallel',
      this.config.removeOuterSpacing
    ).render(svg, innerRect, xAxis.scale, yAxis.scale, undefined, false);
  }

  private drawChange(
    svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
    changeRect: Rectangle,
    x: d3.ScaleBand<string>,
    formatter: (d: number) => string
  ) {
    const changeG = svg.append('g').attr('transform', `translate(${changeRect.x},${changeRect.y})`);
    const yChange = d3.scaleLinear().range([changeRect.height, 0]);
    const changeSeries: { label?: string; value: number }[] = [];

    const primaryData = this.config.getDataArray(ChartDataType.primary)!;
    const comparisonData = this.config.getDataArray(ChartDataType.primary, ChartDataComparisonType.comparison)!;

    primaryData.data.forEach((d, i) => {
      const o = comparisonData.data[i];
      const value = d.value! - o.value!;
      changeSeries.push({
        label: d.label,
        value,
      });
    });

    const domain = d3.extent(changeSeries, d => d.value);

    if (domain[0]! > 0) {
      domain[0] = 0;
    }
    if (domain[1]! < 0) {
      domain[1] = 0;
    }

    yChange.domain(domain as [number, number]);

    const yChangeAxis = d3
      .axisLeft(yChange)
      .ticks(2)
      .tickFormat(d => formatter(d.valueOf()))
      .tickSize(-LinearAxisRenderer.TICK_SIZE)
      .tickSizeOuter(0);

    const yAxisElement = changeG.append('g').attr('class', 'axis axis--y').call(yChangeAxis);

    yAxisElement.call(g => g.select('.domain').remove());

    const barPadding = x.bandwidth() * 0.25;
    const zeroLineY = yChange(0);

    changeG
      .selectAll('.bar')
      .data(changeSeries)
      .enter()
      .append('rect')
      .attr('class', () => 'bar')
      .attr('x', d => x(d.label!)! + barPadding)
      .attr('y', d => Math.min(yChange(d.value), zeroLineY))
      .attr('width', x.bandwidth() - 2 * barPadding)
      .attr('height', d => Math.abs(yChange(d.value) - zeroLineY));
    const xChangeAxis = d3.axisBottom(x).tickSizeOuter(0);
    changeG
      .append('g')
      .attr('class', 'axis axis--x')
      .attr('transform', `translate(0,${zeroLineY})`)
      .call(xChangeAxis)
      .call(a => a.selectAll('.tick line').attr('transform', `translate(${x.bandwidth() / 2}, 0)`))
      .selectAll('text')
      .remove();
  }
}
